import type { Permissions } from "@repo/database";
import { useCallback } from "react";
import { EmployeeGroupTypes } from "@repo/database";
import { getDateFNSLocale } from "@repo/utils";
import CryptoJS from "crypto-js";
import { setDefaultOptions } from "date-fns";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useShallow } from "zustand/react/shallow";

import type { Employee } from "~/providers/employee-provider";
import { useEmployee } from "~/hooks/use-employee";
import { useEmployeeGroup } from "~/hooks/use-employee-group";
import { useUser } from "~/hooks/use-user";
import i18n from "~/lib/i18n";
import { usePermissionStore } from "~/stores/use-permission-store";

interface Permission {
  permission: boolean;
  employee?: Employee;
}

export const usePermissionManager = () => {
  const { employees } = useEmployee();

  const { user, isAdmin } = useUser();

  const { t } = useTranslation("common");

  const {
    getCurrentEmployee,
    setCurrentEmployee,
    setSignInOpen,
    setNoPermissionOpen,
    completeSignIn,
  } = usePermissionStore(
    useShallow((state) => ({
      getCurrentEmployee: state.getCurrentEmployee,
      completeSignIn: state.completeSignIn,
      setSignInOpen: state.setSignInOpen,
      setNoPermissionOpen: state.setNoPermissionOpen,
      setCurrentEmployee: state.setCurrentEmployee,
    })),
  );

  const { employeeGroups } = useEmployeeGroup();

  const checkPermission = useCallback(
    (permission: Permissions, employee?: Employee) => {
      const defaultGroup = employeeGroups.find(
        (employeeGroup) => employeeGroup.type === EmployeeGroupTypes.default,
      );

      if (defaultGroup?.permissions.includes(permission) || isAdmin) {
        return true;
      }

      const employeeGroup = employeeGroups.find(
        (e) => e.id === employee?.employeeGroupId,
      );

      if (employeeGroup) {
        const result = employeeGroup.permissions.some(
          (value) => value === permission,
        );
        if (result === true) {
          return true;
        }
      }

      return false;
    },
    [employeeGroups, isAdmin],
  );

  const hasPermission = useCallback(
    async ({
      requiredPermission,
      label,
      isAction = true,
    }: {
      requiredPermission: Permissions;
      label?: string;
      isAction?: boolean;
    }) => {
      const currentEmployee = getCurrentEmployee();

      const defaultGroup = employeeGroups.find(
        (employeeGroup) => employeeGroup.type === EmployeeGroupTypes.default,
      );

      if (isAdmin) {
        return {
          permission: true,
        };
      }

      if (defaultGroup?.permissions.includes(requiredPermission)) {
        return {
          permission: true,
          employee: currentEmployee,
        };
      }

      if (!currentEmployee) {
        return new Promise<Permission>((resolve) => {
          setSignInOpen({
            open: true,
            label,
            resolve: (employee: Employee) => {
              // After sign-in, check the permission again
              const permission = checkPermission(requiredPermission, employee);
              if (!permission) {
                setNoPermissionOpen(true);
              }
              resolve({
                permission,
                employee,
              });
            },
            reject: () => {
              // Handle the case where sign-in was canceled or failed
              resolve({
                permission: false,
              });
            },
          });
        });
      }

      const permission = checkPermission(requiredPermission, currentEmployee);
      if (!permission) {
        setNoPermissionOpen(true);
      }

      return {
        permission,
        employee: currentEmployee,
      };
    },
    [
      checkPermission,
      employeeGroups,
      getCurrentEmployee,
      isAdmin,
      setNoPermissionOpen,
      setSignInOpen,
    ],
  );

  const login = (pin: string, isTag?: boolean) => {
    const hash = CryptoJS.SHA256(pin).toString();

    let employee: Employee | undefined;

    if (isTag) {
      employee = employees.find((e) => e.tag === hash);
    } else {
      employee = employees.find((e) => e.pin === hash || e.tag === hash);
    }

    if (employee) {
      setCurrentEmployee(employee);
      completeSignIn(employee);
      i18n.changeLanguage(employee.language).catch((e) => console.error(e));
      setDefaultOptions({
        locale: getDateFNSLocale(employee.language),
      });
      toast.success(
        t("Permissions.logged_in_as", { name: employee.firstName }),
      );
    } else {
      toast.error(t("Permissions.invalid_pin"));
    }
    return employee;
  };

  const logout = () => {
    setCurrentEmployee(undefined);
    i18n.changeLanguage(user.language).catch((e) => console.error(e));
    setDefaultOptions({
      locale: getDateFNSLocale(user.language),
    });
  };

  return {
    currentEmployee: getCurrentEmployee,
    hasPermission,
    checkPermission,
    login,
    logout,
  };
};
